import { z } from 'zod';
import { PaginationParamsSchema, paginatedResponseSchema } from './pagination.types.js';

/**
 * Admin-settable lifecycle status shared by accounts (users) and
 * organizations. This is the enum the platform-admin status toggles
 * accept (`PUT /admin/users/:id/status`, org enable/disable) — a
 * deliberate subset of {@link UserLifecycleStatusSchema}, which adds the
 * server-managed `pending` state an admin can never set directly.
 *
 * - `active` — normal operation.
 * - `disabled` — administratively locked. The server rejects every
 *   authenticated API call from a disabled user with HTTP 403
 *   `account_disabled`, and any request whose active org is disabled
 *   with HTTP 403 `organization_disabled`. The web shell renders a
 *   contact-support screen instead of mounting the AppShell.
 *
 * This is the org's manual lifecycle lever only. Time-limited access
 * (trials, future paid tiers) is modeled as an
 * {@link OrganizationSubscription} window and computed at request time
 * — it is NOT a stored status — so the two can never drift out of sync.
 *
 * Modeled as a text enum (rather than a boolean) so future states
 * (`suspended`, `archived`, …) can be added without another schema
 * change.
 */
export const AccountStatusSchema = z.enum(['active', 'disabled']);
export type AccountStatus = z.infer<typeof AccountStatusSchema>;

/**
 * Full lifecycle status of a user account — {@link AccountStatusSchema}
 * plus the server-managed `pending` state.
 *
 * - `pending` — pre-provisioned by a platform admin (org create with a
 *   `pendingOwner`) and never signed in. The row has no auth identity,
 *   provider account, or credential; it is inert until the person's
 *   first sign-in with a verified matching email claims it, which flips
 *   the status to `active` in the same write. Set only by the server's
 *   pre-provisioning path — the admin status toggle accepts
 *   {@link AccountStatusSchema} and cannot put an account back into
 *   `pending`.
 * - `active` / `disabled` — see {@link AccountStatusSchema}.
 *
 * Kept separate from `AccountStatusSchema` (rather than widening it) so
 * the admin-settable surface and the org lifecycle stay closed enums.
 */
export const UserLifecycleStatusSchema = z.enum(['pending', 'active', 'disabled']);
export type UserLifecycleStatus = z.infer<typeof UserLifecycleStatusSchema>;

export const UserSchema = z
  .object({
    id: z.string(),
    providerId: z.string(),
    email: z.string(),
    login: z.string(),
    name: z.string().nullable(),
    avatarUrl: z.string().nullable(),
    createdAt: z.number(),
    emailVerified: z.boolean().optional(),
    emailVerifiedAt: z.number().nullable().optional(),
    onboardingCompletedAt: z.number().nullable().optional(),
    /**
     * The literal `users.is_super_admin` flag. Surfaced only on
     * platform-admin-scoped fetches (`getById`, `listAll`) — the per-org
     * fetches (`getByIdInOrganization`, `listByOrganization`) intentionally
     * omit it so org admins don't see each other's instance-admin status.
     * Optional in the schema so non-admin views can leave it `undefined`.
     */
    isSuperAdmin: z.boolean().optional(),
    /**
     * Lifecycle status. See {@link UserLifecycleStatusSchema} —
     * `pending` marks a pre-provisioned account that has never signed
     * in. Surfaced only on platform-admin-scoped fetches; org-scoped
     * fetches leave it `undefined` (same convention as `isSuperAdmin`).
     */
    status: UserLifecycleStatusSchema.optional(),
  })
  // Tagged so the OpenAPI generator emits one reusable `User` component
  // (referenced by the `users` routes) instead of inlining the shape per
  // operation. See docs/internal/architecture/api-contract.md.
  .meta({ id: 'User' });
export type User = z.infer<typeof UserSchema>;

export const NavigationPreferenceSchema = z.enum(['drill', 'expand', 'flat', 'flyout', 'sessions']);
export type NavigationPreference = z.infer<typeof NavigationPreferenceSchema>;

export const SessionOrderPreferenceSchema = z.enum(['activity', 'created']);
export type SessionOrderPreference = z.infer<typeof SessionOrderPreferenceSchema>;

export const AppearanceModePreferenceSchema = z.enum(['light', 'dark', 'system']);
export type AppearanceModePreference = z.infer<typeof AppearanceModePreferenceSchema>;

/** Account-level UI preferences. Null means the user has not chosen an override yet. */
export const UserPreferencesSchema = z
  .object({
    navigation: NavigationPreferenceSchema.nullable(),
    sessionOrder: SessionOrderPreferenceSchema.nullable(),
    colorTheme: z.string().min(1).nullable(),
    appearanceMode: AppearanceModePreferenceSchema.nullable(),
  })
  .meta({ id: 'UserPreferences' });
export type UserPreferences = z.infer<typeof UserPreferencesSchema>;

/** A partial account-preference update. Omitted fields are left unchanged. */
export const UpdateUserPreferencesSchema = UserPreferencesSchema.partial().refine(
  (value) => Object.keys(value).length > 0,
  { message: 'At least one preference is required' },
);
export type UpdateUserPreferences = z.infer<typeof UpdateUserPreferencesSchema>;

/**
 * Query params for the platform-admin `GET /admin/users` list. Offset
 * pagination via the standard {@link PaginationParamsSchema}, plus an
 * optional free-text search applied to `name`, `email`, `login`, and `id`
 * (case-insensitive). The server trims whitespace and treats an empty `q`
 * as "no filter". Mirrors the `/admin/organizations` surface.
 */
export const AdminListUsersQuerySchema = PaginationParamsSchema.extend({
  q: z.string().trim().optional(),
});
export type AdminListUsersQuery = z.infer<typeof AdminListUsersQuerySchema>;

/** `{ items, page, pageSize, total, totalPages }` envelope for `GET /admin/users`. */
export const AdminListUsersResponseSchema = paginatedResponseSchema(UserSchema);
export type AdminListUsersResponse = z.infer<typeof AdminListUsersResponseSchema>;

/**
 * One linked auth provider for a user, as returned by
 * `GET /admin/users/:id/providers`. `hasAccessToken` reports whether a usable
 * token is stored without ever exposing the token itself.
 */
export const ProviderAccountSchema = z
  .object({
    id: z.string(),
    provider: z.string(),
    providerUserId: z.string(),
    providerData: z.record(z.string(), z.unknown()).nullable(),
    createdAt: z.number(),
    hasAccessToken: z.boolean(),
  })
  .meta({ id: 'ProviderAccount' });
export type ProviderAccount = z.infer<typeof ProviderAccountSchema>;

/** Response for `GET /admin/users/:id/providers` — the user's linked providers. */
export const AdminUserProvidersResponseSchema = z.array(ProviderAccountSchema);
export type AdminUserProvidersResponse = z.infer<typeof AdminUserProvidersResponseSchema>;
